import type { OnlineSignal, FocusSignal } from "../platform.ts";

/**
 * FR-015-090, `contracts/client-api.md` § Offline detection, `research.md` W-24: three
 * inputs (`navigator.onLine`, the stream state and two consecutive transport failures) and
 * three states. `offline` shows the banner within 5 seconds, pauses garbage collection so
 * nothing visible is evicted, serves reads from cache and disables mutating controls. The
 * probe that leaves `offline` is `GET /readyz` with a 5 second timeout, on `online`, on
 * focus and every 15 seconds with jitter.
 */
export type OfflineState = "online" | "degraded" | "offline";

export const PROBE_TIMEOUT_MS = 5_000;
export const PROBE_INTERVAL_MS = 15_000;
export const PROBE_JITTER_MS = 5_000;
export const BANNER_WITHIN_MS = 5_000;
export const TRANSPORT_FAILURE_THRESHOLD = 2;

export interface OfflineMachine {
  readonly state: OfflineState;
  /** A transport-level network failure; two in a row contribute one failure signal. */
  transportFailed(): void;
  /** A transport success clears the consecutive-failure count. */
  transportSucceeded(): void;
  /** The stream reached `reconnecting(2)` or worse (one signal) or recovered (clears it). */
  streamFailing(failing: boolean): void;
  readonly subscribe: (listener: (state: OfflineState) => void) => () => void;
  /** Start listening to the online and focus signals and the probe schedule. */
  start(): () => void;
  /** Run the readiness probe now. */
  probe(): Promise<void>;
}

export interface OfflineDependencies {
  readonly online: OnlineSignal;
  readonly focus: FocusSignal;
  /** `GET /readyz` with the 5 second timeout; resolves true when the instance is serving. */
  readonly ready: (timeoutMs: number) => Promise<boolean>;
  /** Pause and resume the cache's garbage collection while offline. */
  readonly pauseGc: (paused: boolean) => void;
  /** Called when the machine returns to `online`, to drain the write queue. The argument is the
   * state it recovered from: a `degraded` blip never paused a read, an `offline` stretch did, and
   * the two need different amounts of reading again (`recovery.ts`). */
  readonly onReconnect: (from: OfflineState) => void;
  readonly random?: () => number;
}

export function createOfflineMachine(deps: OfflineDependencies): OfflineMachine {
  let state: OfflineState = deps.online.online ? "online" : "offline";
  let consecutiveTransportFailures = 0;
  let transportSignal = false;
  let streamSignal = false;
  let probing = false;
  let probeTimer: ReturnType<typeof setTimeout> | null = null;
  const listeners = new Set<(state: OfflineState) => void>();
  const random = deps.random ?? Math.random;

  const setState = (next: OfflineState) => {
    if (next === state) return;
    const was = state;
    state = next;
    deps.pauseGc(next === "offline");
    for (const listener of listeners) listener(next);
    if (was !== "online" && next === "online") deps.onReconnect(was);
  };
  const evaluate = () => {
    if (!deps.online.online) {
      setState("offline");
      return;
    }
    const signals = Number(transportSignal) + Number(streamSignal);
    if (signals >= 2) setState("offline");
    else if (signals === 1) setState("degraded");
    else setState("online");
  };
  const scheduleProbe = () => {
    if (probeTimer) clearTimeout(probeTimer);
    probeTimer = setTimeout(() => {
      void probe();
    }, PROBE_INTERVAL_MS + Math.floor(random() * PROBE_JITTER_MS));
  };
  const probe = async () => {
    if (probing) return;
    probing = true;
    try {
      if (await deps.ready(PROBE_TIMEOUT_MS)) {
        consecutiveTransportFailures = 0;
        transportSignal = false;
        streamSignal = false;
        evaluate();
      } else if (state !== "online") {
        transportSignal = true;
        evaluate();
      }
    } finally {
      probing = false;
      if (state !== "online") scheduleProbe();
    }
  };

  return {
    get state() {
      return state;
    },
    transportFailed() {
      consecutiveTransportFailures += 1;
      if (consecutiveTransportFailures >= TRANSPORT_FAILURE_THRESHOLD) {
        transportSignal = true;
        evaluate();
        if (state !== "online" && probeTimer === null) scheduleProbe();
      }
    },
    transportSucceeded() {
      consecutiveTransportFailures = 0;
      if (transportSignal) {
        transportSignal = false;
        evaluate();
      }
    },
    streamFailing(failing) {
      streamSignal = failing;
      evaluate();
      if (state !== "online" && probeTimer === null) scheduleProbe();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    start() {
      const unsubscribeOnline = deps.online.subscribe((online) => {
        if (!online) {
          evaluate();
          scheduleProbe();
        } else void probe();
      });
      const unsubscribeFocus = deps.focus.subscribe(() => {
        if (state !== "online") void probe();
      });
      evaluate();
      if (state !== "online") scheduleProbe();
      return () => {
        unsubscribeOnline();
        unsubscribeFocus();
        if (probeTimer) clearTimeout(probeTimer);
        probeTimer = null;
      };
    },
    probe,
  };
}
